import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Check, ChevronRight, ClipboardList, Droplet, Fuel, MapPin, Plane, Radio, WifiOff,
} from "lucide-react";
import { api, ApiError, type Overview as OverviewData, type OverviewAttention, type OverviewFlying } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePolling } from "../lib/usePolling";
import { formatHours } from "../lib/rounding";
import { Alert, PageTitle, Panel, Pill, StatTile, StatusChip } from "../components/ui";
import {
  PHASE_LABEL, duration, minutesSince, placeName, relativeTime, timeOfDay, unitAbbrev,
} from "../lib/missionFormat";

/**
 * A status board, not a dashboard of charts: it answers "is everything all
 * right, and what is happening?" in the time it takes to glance at it.
 */
export function Overview() {
  const auth = useAuth();
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setData(await api.getOverview());
      setError(null);
    } catch (err) {
      // A failed refresh keeps what is on screen — stale beats empty.
      if (data === null) setError(err instanceof ApiError ? err.message : "Couldn't load the overview.");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same cadence as a live mission: anything flying is what this page is for.
  usePolling(load, 15_000);

  if (auth.status !== "authenticated") return null;

  return (
    <div className="flex flex-col gap-9">
      <PageTitle detail={data ? `Updated ${relativeTime(data.generatedAt)}` : undefined}>
        {auth.me.organization.name}
      </PageTitle>

      {error && <Alert>{error}</Alert>}
      {!data && !error && <p className="text-[0.95rem] text-ink-soft">Loading…</p>}

      {data && (
        <>
          <GettingStarted setup={data.setup} />
          <FlyingNow flying={data.flying} staleAfter={data.staleAfterMinutes} />
          {data.attention.length > 0 && <NeedsAttention items={data.attention} />}
          <LastDay stats={data.last24h} />
          <Fleet fleet={data.fleet} />
        </>
      )}
    </div>
  );
}

// ─── Getting started ────────────────────────────────────────────────────

const HIDE_KEY = "firefly.hideGettingStarted";

/**
 * What a new organisation still has to do, in the order it matters. It leaves
 * by itself once everything is done, and can be hidden sooner: what is hidden
 * is remembered per browser, never on the server, because it is a courtesy and
 * not state anyone else needs.
 */
function GettingStarted({ setup }: { setup: OverviewData["setup"] }) {
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const steps = [
    {
      done: setup.aircraft,
      title: "Add your aircraft",
      detail: "iPads pick from this list when a pilot starts a mission.",
      to: "/app/fleet",
      action: "Add aircraft",
    },
    {
      done: setup.device,
      title: "Sign in on an iPad",
      detail: "Open FireFly on the iPad and sign in with this account's email and password.",
      to: "/app/sessions",
      action: "See devices",
    },
    {
      done: setup.mission,
      title: "Log a mission",
      detail: "Missions appear here within a minute of the iPad having signal.",
    },
    {
      done: setup.odr,
      title: "Set the iPad and hours rules",
      detail: "Until you publish, each iPad uses the settings in its own app.",
      to: "/app/settings/odr",
      action: "Open ODR",
    },
    {
      done: setup.company,
      title: "Add your logo and business number",
      detail: "They print on the sheets you export.",
      to: "/app/settings",
      action: "Open settings",
    },
  ];

  const left = steps.filter((s) => !s.done).length;
  if (left === 0 || hidden) return null;

  const hide = () => {
    setHidden(true);
    try {
      localStorage.setItem(HIDE_KEY, "1");
    } catch {
      /* hidden for this visit only */
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-4 mb-2.5 px-1">
        <p className="section-label">Getting started · {steps.length - left} of {steps.length} done</p>
        <button type="button" onClick={hide} className="text-[0.9rem] text-ink-soft hover:text-ink underline-offset-4 hover:underline">
          Hide
        </button>
      </div>
      <Panel>
        <ol className="divide-y divide-rule-soft">
          {steps.map((s) => (
            <li key={s.title} className="flex items-center gap-4 px-5 py-4">
              <span
                aria-hidden
                className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${
                  s.done ? "bg-good text-on-accent" : "border-2 border-rule"
                }`}
              >
                {s.done && <Check size={16} strokeWidth={3} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-[1rem] font-semibold ${s.done ? "text-ink-soft" : "text-ink"}`}>
                  {s.title}
                  {s.done && <span className="sr-only"> (done)</span>}
                </p>
                {!s.done && <p className="mt-0.5 text-[0.9rem] text-ink-soft">{s.detail}</p>}
              </div>
              {!s.done && s.to && (
                <Link
                  to={s.to}
                  className="h-9 px-4 shrink-0 inline-flex items-center rounded-lg bg-panel-raised text-[0.9rem] font-semibold text-ink hover:brightness-110"
                >
                  {s.action}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </Panel>
    </section>
  );
}

// ─── Flying now ─────────────────────────────────────────────────────────

function FlyingNow({ flying, staleAfter }: { flying: OverviewFlying[]; staleAfter: number }) {
  return (
    <section>
      <p className="section-label mb-2.5 px-1">Flying now</p>
      {flying.length === 0 ? (
        <Panel className="px-5 py-4 text-[1rem] text-ink-soft">Nothing flying right now.</Panel>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {flying.map((m) => (
            <FlyingCard key={m.id} mission={m} staleAfter={staleAfter} />
          ))}
        </div>
      )}
    </section>
  );
}

function FlyingCard({ mission: m, staleAfter }: { mission: OverviewFlying; staleAfter: number }) {
  const silentFor = minutesSince(m.lastContactAt);
  // Aircraft lose signal for hours. Past this the card must say so rather than
  // keep presenting an old phase as if it were current.
  const stale = silentFor > staleAfter;

  return (
    <Link
      to={`/app/missions/${m.id}`}
      className="group block bg-panel border border-rule-soft rounded-xl p-5 hover:bg-panel-raised transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono font-bold text-[1.15rem] text-ink">{m.registration ?? m.callsign ?? "—"}</span>
            {m.registration && m.callsign && (
              <span className="text-[1rem] font-semibold text-ink-soft">{m.callsign}</span>
            )}
          </div>
          <p className="mt-0.5 text-[1rem] text-ink truncate">{m.missionName}</p>
        </div>
        <Pill className={stale ? "bg-muted/15 text-ink-soft" : m.phase === "inFlight" ? "bg-good/15 text-good" : "bg-accent/20 text-accent-text"}>
          {stale ? "Last known: " : ""}
          {PHASE_LABEL[m.phase] ?? m.phase}
        </Pill>
      </div>

      <div className="mt-4 flex gap-x-5 gap-y-1 flex-wrap text-[0.9rem] text-ink-soft tabular-nums">
        {m.pilotName && <span>{m.pilotName}</span>}
        <span>Started {timeOfDay(m.startedAt)} · {duration(minutesSince(m.startedAt))}</span>
        <span className="inline-flex items-center gap-1">
          <Droplet size={15} strokeWidth={2.3} aria-hidden />
          {m.dropCount} {m.dropCount === 1 ? "drop" : "drops"}
        </span>
      </div>

      <div className={`mt-3 pt-3 border-t border-rule-soft flex items-center gap-2 text-[0.9rem] ${stale ? "text-danger" : "text-ink-soft"}`}>
        {stale ? <WifiOff size={16} strokeWidth={2.3} aria-hidden /> : <Radio size={16} strokeWidth={2.3} aria-hidden />}
        {stale
          ? `No signal since ${timeOfDay(m.lastContactAt)} (${duration(silentFor)})`
          : `In contact · heard ${relativeTime(m.lastContactAt)}`}
        <ChevronRight size={18} className="ml-auto text-ink-faint group-hover:text-ink-soft" aria-hidden />
      </div>
    </Link>
  );
}

// ─── Needs attention ────────────────────────────────────────────────────

const attentionLink = (a: OverviewAttention) =>
  a.link.to === "mission" ? `/app/missions/${a.link.id}` : a.link.to === "fleet" ? "/app/fleet" : "/app/sessions";

function NeedsAttention({ items }: { items: OverviewAttention[] }) {
  return (
    <section>
      <p className="section-label mb-2.5 px-1">Needs attention</p>
      <Panel>
        <ul className="divide-y divide-rule-soft">
          {items.map((a, i) => (
            <li key={i}>
              <Link
                to={attentionLink(a)}
                className="flex items-center gap-4 px-5 py-4 hover:bg-panel-raised transition-colors first:rounded-t-xl last:rounded-b-xl"
              >
                <span className="grid place-items-center w-10 h-10 rounded-lg bg-danger/15 text-danger shrink-0" aria-hidden>
                  <AlertTriangle size={20} strokeWidth={2.3} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[1rem] font-semibold text-ink">{a.title}</p>
                  <p className="mt-0.5 text-[0.9rem] text-ink-soft">{a.detail}</p>
                </div>
                <ChevronRight size={20} className="text-ink-faint shrink-0" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </section>
  );
}

// ─── Last 24 hours ──────────────────────────────────────────────────────

function LastDay({ stats }: { stats: OverviewData["last24h"] }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2.5 px-1">
        <p className="section-label">Last 24 hours</p>
        <Link to="/app/missions" className="text-[0.9rem] font-semibold text-accent-text hover:underline underline-offset-4">
          All missions
        </Link>
      </div>
      <Panel className="grid grid-cols-2 sm:grid-cols-4 px-2 py-1">
        <StatTile icon={ClipboardList} label="Missions" value={String(stats.missions)} />
        <StatTile icon={Plane} label="Flight time" value={`${formatHours(stats.flightMinutes, stats.flightDp)} h`} />
        <StatTile icon={Droplet} label="Drops" value={String(stats.dropCount)} />
        <StatTile
          icon={Fuel}
          label="Fuel used"
          value={`${stats.fuelUsed.toLocaleString()} ${unitAbbrev(stats.fuelUnit)}`}
        />
      </Panel>
    </section>
  );
}

// ─── Fleet ──────────────────────────────────────────────────────────────

function Fleet({ fleet }: { fleet: OverviewData["fleet"] }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2.5 px-1">
        <p className="section-label">Fleet</p>
        <Link to="/app/fleet" className="text-[0.9rem] font-semibold text-accent-text hover:underline underline-offset-4">
          Manage aircraft
        </Link>
      </div>
      <Panel>
        {fleet.length === 0 ? (
          <p className="px-5 py-4 text-[1rem] text-ink-soft">
            No aircraft yet. <Link to="/app/fleet" className="font-semibold text-accent-text">Add one</Link> to get started.
          </p>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {fleet.map((a) => {
              const where = a.lastPosition
                ? placeName(a.lastPosition.latitude, a.lastPosition.longitude, a.lastPosition.suburb)
                : null;
              const row = (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="font-mono font-bold text-[1.05rem] text-ink">{a.registration}</span>
                      {a.callsign && <span className="text-[1rem] font-semibold text-ink-soft">{a.callsign}</span>}
                      {a.flyingMissionId ? <Pill className="bg-good/15 text-good">Flying</Pill> : <StatusChip status={a.status} />}
                    </div>
                    <p className="mt-1 inline-flex items-center gap-1.5 text-[0.9rem] text-ink-soft">
                      <MapPin size={15} strokeWidth={2.3} aria-hidden />
                      {where && a.lastPosition
                        ? `${where} · ${relativeTime(a.lastPosition.at)}`
                        : "No position recorded yet"}
                    </p>
                  </div>
                  {a.flyingMissionId && <ChevronRight size={20} className="text-ink-faint shrink-0" aria-hidden />}
                </>
              );
              return (
                <li key={a.id}>
                  {a.flyingMissionId ? (
                    <Link
                      to={`/app/missions/${a.flyingMissionId}`}
                      className="flex items-center gap-4 px-5 py-4 hover:bg-panel-raised transition-colors"
                    >
                      {row}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-4 px-5 py-4">{row}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </section>
  );
}
